/**
 * Calendar Grid Component
 *
 * Enhanced calendar with week/month views and drag-drop support.
 */

import { useMemo, useCallback, useState } from 'react';
import { Typography, Badge, Tooltip, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { instancesApi } from '@api/index';
import type { ProcessInstance, ProcessStatus } from '@/types';

const { Text } = Typography;

interface CalendarGridProps {
  currentDate: Dayjs;
  view: 'month' | 'week';
  instancesByDate: Map<string, ProcessInstance[]>;
  onDateSelect: (date: Dayjs) => void;
  onTaskClick: (task: ProcessInstance) => void;
  selectedDate: Dayjs | null;
}

const statusColors: Record<ProcessStatus, string> = {
  pending: 'var(--text-tertiary)',
  in_progress: 'var(--status-info)',
  completed: 'var(--status-success)',
  postponed: 'var(--status-warning)',
  delayed: 'var(--status-error)',
  cancelled: '#bfbfbf',
  ignored: 'var(--text-muted)',
};

export default function CalendarGrid({
  currentDate,
  view,
  instancesByDate,
  onDateSelect,
  onTaskClick,
  selectedDate,
}: CalendarGridProps) {
  const queryClient = useQueryClient();
  const [draggedTask, setDraggedTask] = useState<ProcessInstance | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);

  // Calculate the days to display
  const days = useMemo(() => {
    if (view === 'week') {
      // Get start of week (Sunday)
      const startOfWeek = currentDate.startOf('week');
      return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
    } else {
      // Month view - get full calendar grid
      const startOfMonth = currentDate.startOf('month');
      const endOfMonth = currentDate.endOf('month');
      const startOfCalendar = startOfMonth.startOf('week');
      const endOfCalendar = endOfMonth.endOf('week');

      const daysArray: Dayjs[] = [];
      let day = startOfCalendar;
      while (day.isBefore(endOfCalendar) || day.isSame(endOfCalendar, 'day')) {
        daysArray.push(day);
        day = day.add(1, 'day');
      }
      return daysArray;
    }
  }, [currentDate, view]);

  // Group days into weeks for month view
  const weeks = useMemo(() => {
    if (view === 'week') return [days];
    const result: Dayjs[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days, view]);

  // Mutation for rescheduling tasks
  const rescheduleMutation = useMutation({
    mutationFn: async ({ taskId, newDate }: { taskId: string; newDate: string }) => {
      return instancesApi.update(taskId, { dueDate: newDate });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['instances'] });
      const previousData = queryClient.getQueryData(['instances', 'calendar']);
      return { previousData };
    },
    onError: () => {
      message.error('Failed to reschedule task');
    },
    onSuccess: () => {
      message.success('Task rescheduled');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  const handleDragStart = useCallback((e: React.DragEvent, task: ProcessInstance) => {
    setDraggedTask(task);
    e.dataTransfer.setData('text/plain', task.id);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDate(dateStr);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTargetDate(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dateStr: string) => {
      e.preventDefault();
      setDropTargetDate(null);

      const taskId = e.dataTransfer.getData('text/plain');
      if (taskId && draggedTask) {
        // Don't reschedule if dropped on same date
        if (draggedTask.dueDate.split('T')[0] === dateStr) {
          setDraggedTask(null);
          return;
        }

        rescheduleMutation.mutate({ taskId, newDate: dateStr });
      }
      setDraggedTask(null);
    },
    [draggedTask, rescheduleMutation]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedTask(null);
    setDropTargetDate(null);
  }, []);

  const isToday = (date: Dayjs) => date.isSame(dayjs(), 'day');
  const isCurrentMonth = (date: Dayjs) => date.month() === currentDate.month();
  const isSelected = (date: Dayjs) => selectedDate && date.isSame(selectedDate, 'day');

  const renderDayCell = useCallback(
    (date: Dayjs) => {
      const dateStr = date.format('YYYY-MM-DD');
      const instances = instancesByDate.get(dateStr) || [];
      const isDropTarget = dropTargetDate === dateStr;

      return (
        <div
          key={dateStr}
          onClick={() => onDateSelect(date)}
          onDragOver={(e) => handleDragOver(e, dateStr)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, dateStr)}
          style={{
            flex: 1,
            minHeight: view === 'week' ? 400 : 120,
            padding: 8,
            backgroundColor: isDropTarget
              ? 'var(--status-info-subtle)'
              : isSelected(date)
              ? '#f0f5ff'
              : isToday(date)
              ? 'var(--status-warning-subtle)'
              : isCurrentMonth(date)
              ? '#fff'
              : '#fafafa',
            border: isDropTarget
              ? '2px dashed var(--status-info)'
              : isSelected(date)
              ? '2px solid var(--status-info)'
              : '1px solid #f0f0f0',
            cursor: 'pointer',
            overflow: 'hidden',
            transition: 'all 0.2s',
          }}
        >
          {/* Date number */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 4,
            }}
          >
            <Text
              strong={isToday(date)}
              style={{
                color: isCurrentMonth(date) ? 'inherit' : '#bfbfbf',
                fontSize: view === 'week' ? 16 : 14,
              }}
            >
              {date.date()}
              {view === 'week' && (
                <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
                  {date.format('ddd')}
                </Text>
              )}
            </Text>
            {instances.length > 0 && (
              <Badge count={instances.length} size="small" />
            )}
          </div>

          {/* Tasks */}
          <div style={{ overflow: 'auto', maxHeight: view === 'week' ? 350 : 80 }}>
            {instances.slice(0, view === 'week' ? 20 : 3).map((task) => (
              <Tooltip
                key={task.id}
                title={
                  <div>
                    <div>{task.template?.title}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>
                      {task.status.replace('_', ' ')}
                    </div>
                  </div>
                }
              >
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick(task);
                  }}
                  style={{
                    padding: '2px 6px',
                    marginBottom: 2,
                    borderRadius: 4,
                    backgroundColor: statusColors[task.status] + '20',
                    borderLeft: `3px solid ${statusColors[task.status]}`,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'grab',
                    opacity: draggedTask?.id === task.id ? 0.5 : 1,
                  }}
                >
                  {task.template?.title || 'Untitled'}
                </div>
              </Tooltip>
            ))}
            {instances.length > (view === 'week' ? 20 : 3) && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                +{instances.length - (view === 'week' ? 20 : 3)} more
              </Text>
            )}
          </div>
        </div>
      );
    },
    [
      instancesByDate,
      dropTargetDate,
      selectedDate,
      currentDate,
      view,
      onDateSelect,
      onTaskClick,
      draggedTask,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleDragStart,
      handleDragEnd,
    ]
  );

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
      {/* Day headers */}
      <div
        style={{
          display: 'flex',
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            style={{
              flex: 1,
              padding: '8px 0',
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      {weeks.map((week, weekIndex) => (
        <div key={weekIndex} style={{ display: 'flex' }}>
          {week.map((day) => renderDayCell(day))}
        </div>
      ))}
    </div>
  );
}
